import React, { useEffect, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { Download, FileDown, Search, X, Lock } from "lucide-react";
import api, { formatRp, notifyError } from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";
import { useBranding } from "@/context/BrandingContext";
import { useFeatureFlag } from "@/hooks/useFeatureFlag";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import ReportsAdvanced from "@/pages/admin/ReportsAdvanced";

const PAGE_SIZE = 20;

function toCsv(rows, headers) {
  const csvHeaders = headers.map((h) => h.label).join(",");
  const lines = rows.map((r) =>
    headers.map((h) => {
      const v = r[h.key];
      if (v == null) return "";
      const str = String(v).replace(/"/g, '""');
      return /[,"\n]/.test(str) ? `"${str}"` : str;
    }).join(",")
  );
  return [csvHeaders, ...lines].join("\n");
}

function download(filename, content, type = "text/csv;charset=utf-8") {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

export default function AdminReports() {
  const { branding } = useBranding();
  const advFeature = useFeatureFlag("reports.sales_advanced");
  const today = new Date();
  const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
  const [tab, setTab] = useState("basic");
  const [start, setStart] = useState(monthStart.toISOString().slice(0, 10));
  const [end, setEnd] = useState(today.toISOString().slice(0, 10));
  const [report, setReport] = useState(null);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const debounceRef = useRef(null);

  // debounce 400ms + reset ke page 1 saat keyword berubah
  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => { setDebouncedSearch(search); setPage(1); }, 400);
    return () => clearTimeout(debounceRef.current);
  }, [search]);

  const baseParams = useCallback(() => ({
    start: new Date(start).toISOString(),
    end: new Date(end + "T23:59:59").toISOString(),
    ...(debouncedSearch.trim() ? { search: debouncedSearch.trim() } : {}),
  }), [start, end, debouncedSearch]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/reports/sales", {
        params: { ...baseParams(), page, limit: PAGE_SIZE },
      });
      setReport(data);
    } catch (e) { notifyError(e); }
  }, [baseParams, page]);

  useEffect(() => { load(); }, [load]);

  const fetchAll = async () => {
    const { data } = await api.get("/admin/reports/sales", { params: baseParams() });
    return data;
  };

  const exportCsv = async () => {
    try {
      const full = await fetchAll();
      const headers = [
        { key: "order_number", label: "No Pesanan" },
        { key: "created_at", label: "Tanggal" },
        { key: "user_name", label: "Pelanggan" },
        { key: "user_email", label: "Email" },
        { key: "status", label: "Status" },
        { key: "subtotal", label: "Subtotal" },
        { key: "shipping_cost", label: "Ongkir" },
        { key: "discount", label: "Diskon" },
        { key: "total", label: "Total" },
      ];
      download(`laporan-${start}-${end}.csv`, toCsv(full.orders, headers));
    } catch (e) { notifyError(e); }
  };

  const exportPdf = async () => {
    let full;
    try { full = await fetchAll(); } catch (e) { notifyError(e); return; }
    const origin = window.location.origin;
    // SECURITY: HTML-escape all dynamic values to prevent stored XSS
    const esc = (v) => String(v ?? "").replace(/[&<>"']/g, (c) => ({
      "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;",
    }[c]));
    const safeStart = esc(start);
    const safeEnd = esc(end);
    const brandName = esc(branding.general.name);
    const brandTagline = esc(branding.general.tagline);
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Laporan Penjualan ${brandName}</title>
      <style>
        body{font-family:'Helvetica Neue',Arial,sans-serif;padding:32px;color:#4A2E1F}
        .head{display:flex;align-items:center;gap:14px;border-bottom:3px solid #F9C846;padding-bottom:16px;margin-bottom:20px}
        .head img{width:64px;height:64px}
        .brand{font-weight:800;font-size:24px;letter-spacing:-0.02em}
        .tagline{font-size:11px;text-transform:uppercase;letter-spacing:3px;color:#5A8F3D;font-weight:700}
        h1{color:#4A2E1F;font-size:20px;margin:0 0 4px}
        .period{color:#7A5440;font-size:14px}
        .summary{display:flex;gap:12px;margin:20px 0}
        .summary > div{flex:1;background:#FFF6DF;border:1px solid #F9C846;border-radius:12px;padding:14px}
        .summary .lbl{font-size:11px;text-transform:uppercase;color:#7A5440;font-weight:700;letter-spacing:1px}
        .summary .val{font-size:22px;font-weight:800;margin-top:4px;color:#4A2E1F}
        table{width:100%;border-collapse:collapse;font-size:12px;margin-top:8px}
        th,td{border:1px solid #E5E5E5;padding:8px 10px;text-align:left}
        th{background:#F9C846;color:#4A2E1F;font-weight:700}
        tbody tr:nth-child(even){background:#FFFDF5}
        .footer{margin-top:30px;padding-top:14px;border-top:1px solid #E5E5E5;font-size:11px;color:#7A5440;text-align:center}
      </style>
      </head><body>
      <div class="head">
        <img src="${esc(origin)}/brand/mark.svg" alt="logo" />
        <div>
          <div class="brand">${brandName}</div>
          <div class="tagline">${brandTagline}</div>
        </div>
      </div>
      <h1>Laporan Penjualan</h1>
      <div class="period">Periode: <b>${safeStart}</b> s.d. <b>${safeEnd}</b></div>
      <div class="summary">
        <div><div class="lbl">Total Pesanan</div><div class="val">${esc(full.total_orders)}</div></div>
        <div><div class="lbl">Total Omzet</div><div class="val">${esc(formatRp(full.total_revenue))}</div></div>
      </div>
      <table><thead><tr><th>No Pesanan</th><th>Tanggal</th><th>Pelanggan</th><th>Status</th><th>Total</th></tr></thead><tbody>
      ${full.orders.map((o) => `<tr><td>${esc(o.order_number)}</td><td>${esc(new Date(o.created_at).toLocaleDateString("id-ID"))}</td><td>${esc(o.user_name || "")}</td><td>${esc(o.status)}</td><td>${esc(formatRp(o.total))}</td></tr>`).join("")}
      </tbody></table>
      <div class="footer">Dicetak dari ${brandName} · ${esc(new Date().toLocaleString("id-ID"))}</div>
      </body></html>`;

    // SECURITY: Blob URL, bukan document.write()
    const blob = new Blob([html], { type: "text/html;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const w = window.open(url, "_blank", "noopener,noreferrer");
    if (!w) {
      toast.error("Popup diblokir. Mohon izinkan popup untuk fitur cetak.");
      URL.revokeObjectURL(url);
      return;
    }
    w.addEventListener("load", () => {
      try { w.print(); } catch (_) { /* user can press Ctrl+P manually */ }
    });
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  };

  const totalPages = report ? Math.max(1, Math.ceil((report.total_orders || 0) / PAGE_SIZE)) : 1;
  const advEnabled = advFeature.enabled;

  return (
    <AdminLayout>
      <h1 className="font-display text-3xl font-black mb-4">Laporan Penjualan</h1>

      {/* Tabs */}
      <div className="flex gap-2 mb-4" data-testid="reports-tabs">
        <button onClick={() => setTab("basic")} data-testid="reports-tab-basic"
          className={`text-sm font-bold px-4 py-2 rounded-xl ${tab === "basic" ? "brand-bg-primary text-white" : "bg-white border border-[#E5E5E5]"}`}>
          Laporan Dasar
        </button>
        <button onClick={() => setTab("advanced")} data-testid="reports-tab-advanced"
          className={`text-sm font-bold px-4 py-2 rounded-xl inline-flex items-center gap-1.5 ${tab === "advanced" ? "brand-bg-primary text-white" : "bg-white border border-[#E5E5E5]"}`}>
          {!advEnabled && <Lock size={13} />} Laporan Lengkap
        </button>
      </div>

      {tab === "advanced" ? (
        advEnabled ? (
          <ReportsAdvanced start={start} end={end} setStart={setStart} setEnd={setEnd} />
        ) : (
          <div className="bg-white border border-[#E5E5E5] rounded-2xl p-10 text-center max-w-xl mx-auto" data-testid="reports-advanced-upgrade">
            <div className="w-14 h-14 rounded-full bg-[#FAF9F6] border border-[#E5E5E5] grid place-items-center mx-auto mb-4">
              <Lock size={22} className="text-[#5C5C5C]" />
            </div>
            <h2 className="font-display text-xl font-black mb-2">Laporan Lengkap — Fitur ENTERPRISE</h2>
            <p className="text-sm text-[#5C5C5C] mb-5">
              Dapatkan summary metrics, grafik tren harian/mingguan/bulanan, produk &amp; pelanggan terlaris,
              perbandingan periode, dan export laporan dengan upgrade ke paket ENTERPRISE.
            </p>
            <Link to="/admin/langganan">
              <Button className="brand-btn-primary rounded-xl font-bold" data-testid="reports-upgrade-btn">Lihat Paket Langganan</Button>
            </Link>
          </div>
        )
      ) : (
        <>
          <div className="bg-white border border-[#E5E5E5] rounded-2xl p-5 mb-4 flex flex-wrap gap-3 items-end">
            <div><Label>Dari</Label><Input type="date" value={start} onChange={(e) => { setStart(e.target.value); setPage(1); }} className="rounded-xl" data-testid="report-start" /></div>
            <div><Label>Sampai</Label><Input type="date" value={end} onChange={(e) => { setEnd(e.target.value); setPage(1); }} className="rounded-xl" data-testid="report-end" /></div>
            <div className="flex-1 min-w-[220px]">
              <Label>Cari</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C5C5C]" size={15} />
                <Input value={search} onChange={(e) => setSearch(e.target.value)}
                  placeholder="No pesanan, pelanggan, atau produk…"
                  className="pl-9 pr-9 rounded-xl" data-testid="report-search-input" />
                {search && (
                  <button onClick={() => setSearch("")} data-testid="report-search-clear"
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[#5C5C5C] hover:text-black">
                    <X size={15} />
                  </button>
                )}
              </div>
            </div>
            <div className="ml-auto flex gap-2">
              <Button variant="outline" onClick={exportCsv} disabled={!report} className="rounded-xl" data-testid="report-export-csv"><FileDown size={14} /> Excel/CSV</Button>
              <Button variant="outline" onClick={exportPdf} disabled={!report} className="rounded-xl" data-testid="report-export-pdf"><Download size={14} /> PDF</Button>
            </div>
          </div>

          {report && (
            <>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="bg-white border border-[#E5E5E5] rounded-2xl p-5">
                  <div className="text-xs uppercase tracking-widest text-[#5C5C5C] font-bold">Total Pesanan</div>
                  <div className="font-display text-3xl font-black mt-1" data-testid="report-total-orders">{report.total_orders}</div>
                </div>
                <div className="bg-white border border-[#E5E5E5] rounded-2xl p-5">
                  <div className="text-xs uppercase tracking-widest text-[#5C5C5C] font-bold">Total Omzet</div>
                  <div className="font-display text-3xl font-black mt-1 text-[#E85D04]" data-testid="report-total-revenue">{formatRp(report.total_revenue)}</div>
                </div>
              </div>

              <div className="bg-white border border-[#E5E5E5] rounded-2xl overflow-x-auto">
                <table className="w-full text-sm" data-testid="report-table">
                  <thead>
                    <tr className="text-left text-[10px] uppercase tracking-widest text-[#5C5C5C] font-bold border-b border-[#E5E5E5]">
                      <th className="px-4 py-3">No</th>
                      <th className="px-4 py-3">Tanggal</th>
                      <th className="px-4 py-3">Pelanggan</th>
                      <th className="px-4 py-3">Status</th>
                      <th className="px-4 py-3">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.orders.map((o) => (
                      <tr key={o.id} className="border-b border-[#E5E5E5] last:border-0 hover:bg-[#FAF9F6]">
                        <td className="px-4 py-3">
                          <Link to={`/admin/pesanan/${o.id}`} data-testid={`report-order-link-${o.order_number}`}
                            className="font-mono text-xs font-bold text-[color:var(--brand-primary,#E85D04)] hover:underline">
                            {o.order_number}
                          </Link>
                        </td>
                        <td className="px-4 py-3 text-xs">{new Date(o.created_at).toLocaleDateString("id-ID")}</td>
                        <td className="px-4 py-3">{o.user_name}</td>
                        <td className="px-4 py-3"><span className="text-[10px] uppercase font-bold px-2 py-1 rounded bg-[#FAF9F6]">{o.status}</span></td>
                        <td className="px-4 py-3 font-bold">{formatRp(o.total)}</td>
                      </tr>
                    ))}
                    {report.orders.length === 0 && (
                      <tr><td colSpan={5} className="py-8 text-center text-[#5C5C5C]" data-testid="report-empty">
                        {debouncedSearch ? `Tidak ada transaksi untuk "${debouncedSearch}".` : "Tidak ada data"}
                      </td></tr>
                    )}
                  </tbody>
                </table>
                {totalPages > 1 && (
                  <div className="flex items-center justify-between px-4 py-3 border-t border-[#E5E5E5] text-xs">
                    <Button size="sm" variant="outline" className="rounded-xl" disabled={page <= 1}
                      onClick={() => setPage((p) => p - 1)} data-testid="report-prev-page">Sebelumnya</Button>
                    <span className="text-[#5C5C5C]" data-testid="report-page-info">Hal {page} / {totalPages} · {report.total_orders} pesanan</span>
                    <Button size="sm" variant="outline" className="rounded-xl" disabled={page >= totalPages}
                      onClick={() => setPage((p) => p + 1)} data-testid="report-next-page">Berikutnya</Button>
                  </div>
                )}
              </div>
            </>
          )}
        </>
      )}
    </AdminLayout>
  );
}
